import React, { useEffect, useState } from "react";
import "./StudentManagement.css";
import { useSelector } from "react-redux";
import { ERROR_TOAST, SUCCESS_TOAST } from "../../helpers/toasters";
import { getCoachbatches } from "../../API/coach-mgmt";
import { getBatches } from "../../API/Batches";
import { saveAttendance, getAttendanceByDate } from "../../API/attendence";
import AllocateStudCard from "../../components/allocateCard/allocateCard";
import ExportModal from "./ExportModal";
import ViewAllocatedCards from "../../components/manageCards/manageCards";
import ManageAssesment from "../../components/manageAssesment/manageAssesment";
import ViewAssessmentDetails from "../../components/manageAssesment/viewAssessment";

interface Student {
  _id: string
  name: string
  isActive: boolean
  RollNumber: string
}

const StudMgmt = () => {
  type CourseType = {
    _id: string;
    name: string;
    assessment_levels?: string[];
    certification_levels?: string[];
  };

  const [batches, setBatches] = useState<any[]>([]);
  const [attendance, setAttendance] = useState<{ [key: string]: boolean }>({});
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const [selectedBatchName, setSelectedBatchName] = useState<string | null>(null);
  const [batchDetails, setBatchDetails] = useState<any>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);

  // Simplified dropdown state
  const [showCardDropdown, setShowCardDropdown] = useState(false);
  const [showAssessmentDropdown, setShowAssessmentDropdown] = useState(false);

  // State for directly showing AllocateStudCard
  const [showAllocateCard, setShowAllocateCard] = useState(false);
  const [showViewAllocatedCards, setShowViewAllocatedCards] = useState(false);
  const [showManageAssessment, setShowManageAssessment] = useState(false);
  const [showViewAssessmentDetails, setShowViewAssessmentDetails] = useState(false);

  const [selectedCourse, setSelectedCourse] = useState<CourseType | undefined>(undefined);

  useEffect(() => {
    if (selectedBatch) {
      const course = batches.find(batch => batch._id === selectedBatch)?.course;
      setSelectedCourse(course[0]);
    }
  }, [selectedBatch, batches]);


  const user = useSelector((state: any) => state.auth);

  useEffect(() => {
    if (user.isCoach) {
      getCoachBatches(user._id);
    } else {
      getAdminBatches();
    }
  }, []);

  useEffect(() => {
    
    if (selectedBatch) {
      const batch = batches.find((b) => b._id === selectedBatch);
      if (batch) {
        setBatchDetails(batch);
        setStudents(batch.students || []);
        
        setFilteredStudents(batch.students || []);
        setSelectedBatchName(batch.name);
      }
    }
  }, [selectedBatch, batches]);

  useEffect(() => {
    if (selectedBatchName) {
      const batch = batches.find((b) => b.name === selectedBatchName);
      if (batch) {
        setSelectedBatch(batch._id);
        setStudents(batch.students || []);
        setFilteredStudents(batch.students || []);
      }
    }
  }, [selectedBatchName, batches]);

  const DAYS_MAPPING = {
    1: "Mon",
    2: "Tue",
    3: "Wed",
    4: "Thu",
    5: "Fri",
    6: "Sat",
    7: "Sun"
  };

  const getAdminBatches = () => {
    getBatches()
      .then((data) => {
        setBatches(data);
        
        if (data.length > 0) {
          setSelectedBatch(data[0]._id);
          setSelectedBatchName(data[0].name);
          setStudents(data[0].students || []);
          setFilteredStudents(data[0].students || []);
        }
      })
      .catch(() => ERROR_TOAST("Error while getting batches!"));
  };

  const getCoachBatches = async (_id: string) => {
    try {
      getCoachbatches(_id)
        .then((data) => {
          setBatches(data);
          
          if (data.length > 0) {
            setSelectedBatch(data[0]._id);
            setSelectedBatchName(data[0].name);
            setStudents(data[0].students || []);
            setFilteredStudents(data[0].students || []);
          }
        })
        .catch(() => ERROR_TOAST("Error while getting batches!"));

    } catch (err) {
      console.error("❌ Error fetching coach batches:", err);
      ERROR_TOAST("Error while getting batches!");
    }
  };

  const handleBatchIdChange = (batchId: string) => {
    setSelectedBatch(batchId);
    const batch = batches.find((b) => b._id === batchId);
    if (batch) {
      setSelectedBatchName(batch.name);
    }
  };

  const handleBatchNameChange = (batchName: string) => {
    setSelectedBatchName(batchName);
    const batch = batches.find((b) => b.name === batchName);
    if (batch) {
      setSelectedBatch(batch._id);
    }
  };

  const handleSaveAttendance = async () => {
    if (!selectedBatch) {
      ERROR_TOAST("Please select a batch!");
      return;
    }

    const studentArray = students.map((student) => ({
      studentId: String(student._id),
      present: attendance[student._id] || false,
    }));

    const payload = {
      batch: selectedBatch,
      date: selectedDate,
      student: studentArray,
    };

    try {
      const response = await saveAttendance(payload);
      
      if (response.message === "success") {
        SUCCESS_TOAST("Attendance saved successfully!");
      } else {
        ERROR_TOAST("Error saving attendance");
      }
    } catch (error) {
      console.error("❌ Error saving attendance:", error);
      ERROR_TOAST("Something went wrong!");
    }
  };

  const toggleAttendance = (studentId: string) => {
    setAttendance((prevAttendance) => ({
      ...prevAttendance,
      [studentId]: !prevAttendance[studentId],
    }));
  };

  const formatTimeTo12Hour = (timeString: string) => {
    if (!timeString) return "N/A";

    const [startTime, endTime] = timeString.split(" - ");

    const convertTime = (time: string) => {
      const [hours, minutes] = time.split(":");
      let hourNum = parseInt(hours, 10);
      const period = hourNum >= 12 ? "PM" : "AM";

      if (hourNum === 0) {
        hourNum = 12;
      } else if (hourNum > 12) {
        hourNum -= 12;
      }

      return `${hourNum}:${minutes} ${period}`;
    };

    const formattedStart = convertTime(startTime);
    const formattedEnd = convertTime(endTime);

    return `${formattedStart} - ${formattedEnd}`;
  };

  const handleDateChange = async (date: string) => {
    setSelectedDate(date);

    if (!selectedBatch) {
      console.warn("⚠️ No batch selected, skipping attendance fetch!");
      return;
    }

    setAttendance({});

    try {
      const response = await getAttendanceByDate(selectedBatch, date);

      

      if (response.message === "success" && Array.isArray(response.data)) {
        const fetchedAttendance = response.data.reduce((acc, s) => {
          acc[s.studentId] = s.present ?? false;
          return acc;
        }, {} as { [key: string]: boolean });

        

        setAttendance(fetchedAttendance);
      } else {
        
        setAttendance({});
      }
    } catch (error) {
      console.error("❌ Error fetching attendance:", error);
      ERROR_TOAST("Error fetching attendance!");
    }
  };

  useEffect(() => {
    if (selectedBatch && selectedDate) {
      
      handleDateChange(selectedDate);
    }
  }, [selectedBatch, selectedDate]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query === "") {
      setFilteredStudents(students);
    } else {
      const filtered = students.filter((student) =>
        student.name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredStudents(filtered);
    }
  };

  const handleExportClick = async () => {
    if (!selectedBatch) {
      return ERROR_TOAST("Please select a batch!");
    }

    try {
      const currentDateRecords = students.map(student => ({
        studentId: student._id,
        present: attendance[student._id] || false,
        date: selectedDate
      }));

      setAttendanceRecords(currentDateRecords);
      setShowExportModal(true);
    } catch (error) {
      console.error("❌ Error preparing attendance for export:", error);
      ERROR_TOAST("Failed to prepare attendance for export!");
    }
  };

  // Updated card management handler to directly show AllocateStudCard
  const handleCardOption = (option: string) => {
    setShowCardDropdown(false);

    if (option === 'allocate') {
      // Directly show AllocateStudCard
      setShowAllocateCard(true);
    } else if (option === 'view') {
      // Handle view allocated cards
      setShowViewAllocatedCards(true);
    }
  };

  const handleAssessmentOption = (option: string) => {
    setShowAssessmentDropdown(false);

    if (option === 'create') {
      setShowManageAssessment(true);
    } else if (option === 'view') {
      // Show ViewAssessmentDetails modal
      setShowViewAssessmentDetails(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Close card dropdown if click is outside
      if (showCardDropdown && !target.closest('.card-dropdown-container')) {
        setShowCardDropdown(false);
      }

      // Close assessment dropdown if click is outside
      if (showAssessmentDropdown && !target.closest('.assessment-dropdown-container')) {
        setShowAssessmentDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCardDropdown, showAssessmentDropdown]);

  // Close component handlers
  const handleCloseAllocateCard = () => {
    setShowAllocateCard(false);
  };

  const handleCloseViewAllocatedCards = () => {
    setShowViewAllocatedCards(false);
  };

  const handleCloseManageAssessment = () => {
    setShowManageAssessment(false);
  };

  if (showManageAssessment) {
    return (
      <div>
        
        <ManageAssesment
          handelClose={handleCloseManageAssessment}
          selectedBatchId={selectedBatch || undefined}
          batchStudents={students}
          batchName={selectedBatchName}
          course={selectedCourse}
        />
      </div>
    );
  }

  if (showAllocateCard) {
    return (
      <div>
        {/* Use AllocateStudCard directly instead of AllocateCard */}
        <AllocateStudCard
          handelClose={handleCloseAllocateCard}
          selectedBatchId={selectedBatch || undefined}
          batchStudents={students}
          batchName={selectedBatchName || undefined}
          multipleSelection={true}
        />
      </div>
    );
  }

  if (showViewAllocatedCards) {
    return (
      <div className="student-management">
        {showViewAllocatedCards && (
          <ViewAllocatedCards
            open={showViewAllocatedCards}
            onClose={() => setShowViewAllocatedCards(false)}
            batchId={selectedBatch || ""}
          />
        )}
      </div>
    );
  };

  if (showViewAssessmentDetails) {
    return (
      <div className="student-management">
        {showViewAssessmentDetails && (
          <ViewAssessmentDetails
            open={showViewAssessmentDetails}
            onClose={() => setShowViewAssessmentDetails(false)}
            batchId={selectedBatch || ""}
          />
        )}
      </div>
    );
  };

  return (
    <div className="student-management">
      <div className="student-management-container">
        <div className="student-management-header">
          <h2>Batch Management</h2>
          <div className="action-buttons">
            {/* Card Management Dropdown */}
            <div className="sm-dropdown-container sm-card-dropdown card-dropdown-container">
              <button
                className="sm-dropdown-toggle"
                onClick={() => setShowCardDropdown(!showCardDropdown)}
              >
                Card Management <i className="fas fa-chevron-down"></i>
              </button>
              {showCardDropdown && (
                <div className={`sm-dropdown-menu ${showCardDropdown ? 'show' : ''}`}>
                  <div className="sm-dropdown-item"
                    onClick={() => handleCardOption('allocate')}
                  >
                    Allocate a Card
                  </div>
                  <div className="sm-dropdown-item"
                    onClick={() => handleCardOption('view')}
                  >
                    View Allocated Cards
                  </div>
                </div>
              )}
            </div>

            {/* Assessment Management Dropdown */}
            <div className="sm-dropdown-container sm-assessment-dropdown assessment-dropdown-container">
              <button
                className="sm-dropdown-toggle"
                onClick={() => setShowAssessmentDropdown(!showAssessmentDropdown)}
              >
                Manage Assessments <i className="fas fa-chevron-down"></i>
              </button>
              {showAssessmentDropdown && (
                <div className={`sm-dropdown-menu ${showAssessmentDropdown ? 'show' : ''}`}>
                  <div className="sm-dropdown-item"
                    onClick={() => handleAssessmentOption('create')}
                  >
                    Create Assessment
                  </div>
                  <div className="sm-dropdown-item"
                    onClick={() => handleAssessmentOption('view')}
                  >
                    View Assessments
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="filters-section">
          <div className="filter-group">
            <label>Batch ID</label>
            <select
              onChange={(e) => handleBatchIdChange(e.target.value)}
              value={selectedBatch || ""}
            >
              {batches.map((batch) => (
                <option key={batch._id} value={batch._id}>
                  {batch.batch_id}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Batch Name</label>
            <select
              data-cy="batch-select"
              onChange={(e) => handleBatchNameChange(e.target.value)}
              value={selectedBatchName || ""}
            >
              {batches.map((batch) => (
                <option key={batch._id} value={batch.name}>
                  {batch.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
            />
          </div>
        </div>

        {batchDetails && (
          <div className="batch-info-card">
            <div className="info-item">
              <span className="info-label">Time Slot:</span>
              <span className="info-value">{formatTimeTo12Hour(batchDetails.timeslot) || "N/A"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Course ID:</span>
              <span className="info-value">{batchDetails.course?.[0]?.course_id || "N/A"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Days:</span>
              <span className="info-value">
                {batchDetails.days.map(day => DAYS_MAPPING[day] ?? -1).filter(day => day !== -1).join(", ")}
              </span>
            </div>
          </div>
        )}

        <div className="search-section">
          <input
            type="text"
            placeholder="Search Student Name"
            value={searchQuery}
            onChange={handleSearch}
          />
        </div>

        <div className="students-grid">
          {filteredStudents.length > 0 ? (
            filteredStudents.map((student) => (
              student.isActive &&
              <div
                key={student._id}
                className={`student-card ${attendance[student._id] ? "present" : "absent"}`}
              >
                
                <span className="student-name">{student.name}</span>
                <button
                  onClick={() => toggleAttendance(student._id)}
                  className={`attendance-toggle ${attendance[student._id] ? "present-btn" : "absent-btn"}`}
                >
                  {attendance[student._id] ? "Present" : "Absent"}
                </button>
              </div>
            ))
          ) : (
            <div className="no-students">No students found matching your search.</div>
          )}
        </div>
        
        <div className="action-footer">
          <button className="save-attendance-btn" onClick={handleSaveAttendance}>Save Attendance</button>
          {!user.isCoach && <button className="save-attendance-btn" onClick={handleExportClick}>Export Attendance</button>}
        </div>

        {showExportModal && (
          <ExportModal
            students={students}
            attendanceData={attendanceRecords}
            selectedBatch={selectedBatch}
            batches={batches}
            selectedDate={selectedDate}
            onClose={() => setShowExportModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default StudMgmt;